import { defineStore } from "pinia";
import { getBanners } from "@/api/banners";

/**
 * Vérifie si la date actuelle est entre les dates de début et fin
 * @param {string} startDate - Date de début
 * @param {string} endDate - Date de fin
 * @returns {boolean} True si la date actuelle est dans la plage
 */
function isDateValid(startDate, endDate) {
  if (!startDate || !endDate) return true;

  const start = new Date(startDate);
  const end = new Date(endDate);
  const now = new Date();

  return now >= start && now <= end;
}

export const useBannerStore = defineStore("banner", {
  state: () => ({
    banners: [],
    loading: false,
  }),
  getters: {
    Banners: state => state.banners ?? [],

    /**
     * Retourne les bannières filtrées selon le rôle utilisateur et les dates
     * @param {string} userRole - Le rôle de l'utilisateur ('client', 'veterinaire', etc.)
     * @returns {Array} Bannières filtrées et triées par date (plus récente d'abord)
     */
    contextualBanners: state => userRole => {
      const targetChannel = userRole === "client" ? "esupplyvet" : "supplyvet";

      return state.banners
        .filter(banner => {
          if (banner.channels && banner.channels.length > 0) {
            const hasMatchingChannel = banner.channels.some(channel => channel.includes(targetChannel));
            if (!hasMatchingChannel) {
              return false;
            }
          }

          if (!isDateValid(banner.startDate, banner.endDate)) {
            return false;
          }

          if (banner.active === false) {
            return false;
          }

          return true;
        })
        .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    },

    /**
     * Retourne la première bannière contextuelle (la plus récente)
     * @param {string} userRole - Le rôle de l'utilisateur
     * @returns {Object|null} La bannière à afficher ou null
     */
    activeBanner: state => userRole => {
      const targetChannel = userRole === "client" ? "esupplyvet" : "supplyvet";

      const contextualBanners = state.banners
        .filter(banner => {
          if (banner.channels && banner.channels.length > 0) {
            const hasMatchingChannel = banner.channels.some(channel => channel.includes(targetChannel));
            if (!hasMatchingChannel) {
              return false;
            }
          }

          if (!isDateValid(banner.startDate, banner.endDate)) {
            return false;
          }

          if (banner.active === false) {
            return false;
          }

          return true;
        })
        .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

      return contextualBanners[0] || null;
    },
  },
  actions: {
    /**
     * Récupère les bannières selon le rôle utilisateur
     * @param {string} userRole - Le rôle de l'utilisateur
     */
    async fetchBanners(userRole = null) {
      this.loading = true;
      try {
        const res = await getBanners(userRole);
        this.banners = res.data || [];
      } catch (error) {
        console.error("Erreur lors de la récupération des bannières", error);
        this.banners = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * Remet à zéro les bannières
     */
    resetBanners() {
      this.banners = [];
      this.loading = false;
    },
  },
});
